import { useState } from 'react';
import { ActionIcon, Button, Group, Popover, Select, Stack, Text, TextInput, Tooltip } from '@mantine/core';
import { IconRulerMeasure } from '@tabler/icons-react';

type Formula = 'AREA' | 'WALL_AREA' | 'PERIMETER' | 'VOLUME';

const FORMULA_OPTIONS: Array<{ value: Formula; label: string }> = [
  { value: 'AREA', label: 'Площадь: длина × ширина (м²)' },
  { value: 'WALL_AREA', label: 'Площадь стен: периметр × высота (м²)' },
  { value: 'PERIMETER', label: 'Периметр: 2×(длина+ширина) (м.п.)' },
  { value: 'VOLUME', label: 'Объём: длина × ширина × высота (м³)' },
];

/** Поля, нужные для каждой формулы — под одни показываем высоту, под другие нет. */
const FORMULA_FIELDS: Record<Formula, Array<'length' | 'width' | 'height'>> = {
  AREA: ['length', 'width'],
  WALL_AREA: ['length', 'width', 'height'],
  PERIMETER: ['length', 'width'],
  VOLUME: ['length', 'width', 'height'],
};

function parseNumber(raw: string): number | null {
  const normalized = raw.trim().replace(',', '.');
  if (!/^\d+(\.\d+)?$/.test(normalized)) return null;
  const value = Number(normalized);
  return value > 0 ? value : null;
}

function compute(formula: Formula, length: number | null, width: number | null, height: number | null): number | null {
  switch (formula) {
    case 'AREA':
      return length !== null && width !== null ? length * width : null;
    case 'PERIMETER':
      return length !== null && width !== null ? 2 * (length + width) : null;
    case 'WALL_AREA': {
      // Периметр помещения × высота стен.
      if (length === null || width === null || height === null) return null;
      return 2 * (length + width) * height;
    }
    case 'VOLUME':
      return length !== null && width !== null && height !== null ? length * width * height : null;
  }
}

interface Props {
  /** Вызывается с готовым объёмом (уже отформатированным числом-строкой), когда пользователь нажал «Подставить». */
  onApply: (quantity: string) => void;
  disabled?: boolean;
}

/**
 * Калькулятор объёма по размерам помещения — открывается попапом рядом
 * с полем «Объём» строки сметы, чтобы не считать площадь/периметр в уме
 * или на калькуляторе телефона перед вводом.
 */
export function QuantityCalculatorPopover({ onApply, disabled }: Props) {
  const [opened, setOpened] = useState(false);
  const [formula, setFormula] = useState<Formula>('AREA');
  const [length, setLength] = useState('');
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');

  const fields = FORMULA_FIELDS[formula];
  const result = compute(
    formula,
    parseNumber(length),
    fields.includes('width') ? parseNumber(width) : null,
    fields.includes('height') ? parseNumber(height) : null,
  );

  function handleApply() {
    if (result === null) return;
    // Три знака после запятой — предел точности quantity в БД (Decimal(18,3)).
    onApply(result.toFixed(3));
    setOpened(false);
  }

  return (
    <Popover opened={opened} onChange={setOpened} width={280} position="bottom-start" withArrow shadow="md">
      <Popover.Target>
        <Tooltip label="Рассчитать по размерам">
          <ActionIcon variant="subtle" color="gray" disabled={disabled} onClick={() => setOpened((v) => !v)}>
            <IconRulerMeasure size={14} />
          </ActionIcon>
        </Tooltip>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap="xs">
          <Select
            size="xs"
            label="Формула"
            data={FORMULA_OPTIONS}
            value={formula}
            onChange={(v) => setFormula((v as Formula) ?? formula)}
            allowDeselect={false}
          />
          <Group grow gap="xs">
            <TextInput
              size="xs"
              label={formula === 'PERIMETER' || formula === 'WALL_AREA' ? 'Длина стены A' : 'Длина, м'}
              value={length}
              onChange={(e) => setLength(e.currentTarget.value)}
              autoFocus
            />
            {fields.includes('width') && (
              <TextInput
                size="xs"
                label={formula === 'PERIMETER' || formula === 'WALL_AREA' ? 'Длина стены B' : 'Ширина, м'}
                value={width}
                onChange={(e) => setWidth(e.currentTarget.value)}
              />
            )}
          </Group>
          {fields.includes('height') && (
            <TextInput
              size="xs"
              label="Высота, м"
              value={height}
              onChange={(e) => setHeight(e.currentTarget.value)}
            />
          )}

          <Text size="xs" c="dimmed">
            Результат: {result !== null ? result.toFixed(3) : '—'}
          </Text>

          <Button size="xs" onClick={handleApply} disabled={result === null}>
            Подставить
          </Button>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
